import React, { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';
import './AddEventModal.css';

const EVENT_TYPES = {
  court_hearing: { name: 'Court Hearing', color: '#3b82f6' },
  client_meeting: { name: 'Client Meeting', color: '#eab308' },
  filing_deadline: { name: 'Filing Deadline', color: '#ec4899' },
  trial_date: { name: 'Trial Date', color: '#06b6d4' },
  contract_review: { name: 'Contract Review', color: '#10b981' },
  other: { name: 'Other Task', color: '#f97316' },
};

const CLOSED_EVENT_STATUSES = new Set(['completed', 'cancelled', 'deleted']);

function parseLocalDateTime(dateStr, timeStr = '00:00') {
  if (!dateStr) return null;
  const [year, month, day] = String(dateStr).split('-').map(Number);
  const [hours, minutes] = String(timeStr || '00:00').split(':').map(Number);
  return new Date(year, (month || 1) - 1, day || 1, hours || 0, minutes || 0, 0, 0);
}

function getRangeForDate(dateStr, startTime, endTime) {
  const start = parseLocalDateTime(dateStr, startTime || '00:00');
  if (!start) return null;

  let end = endTime
    ? parseLocalDateTime(dateStr, endTime)
    : new Date(start.getTime() + 60 * 1000);

  if (end <= start) {
    end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
  }

  return { start, end };
}

function hasTimeOverlap(a, b) {
  return a.start < b.end && a.end > b.start;
}

function formatClock(timeStr) {
  if (!timeStr) return 'No time';
  const [h, m] = String(timeStr).split(':').map(Number);
  const hh = Number.isFinite(h) ? h : 0;
  const mm = Number.isFinite(m) ? m : 0;
  const suffix = hh >= 12 ? 'PM' : 'AM';
  const hour12 = hh % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${String(mm).padStart(2, '0')} ${suffix}`;
}

function formatLocalDateKey(dateObj) {
  if (!(dateObj instanceof Date)) return '';
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function AddEventModal({ isOpen, onClose, onSubmit, selectedDate, userRole, clients = [], events = [] }) {
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'court_hearing',
    description: '',
    start_date: '',
    start_time: '09:00',
    end_time: '10:00',
    location: '',
    selected_client_id: '',
    reminder_minutes: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [customTaskType, setCustomTaskType] = useState('');
  const [pendingConflictPayload, setPendingConflictPayload] = useState(null);
  const [conflictingEvents, setConflictingEvents] = useState([]);

  useEffect(() => {
    if (selectedDate && isOpen) {
      const dateStr = formatLocalDateKey(selectedDate);
      setFormData(prev => ({ ...prev, start_date: dateStr }));
    }
  }, [selectedDate, isOpen]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        event_type: 'court_hearing',
        description: '',
        start_date: '',
        start_time: '09:00',
        end_time: '10:00',
        location: '',
        selected_client_id: '',
        reminder_minutes: 0,
      });
      setCustomTaskType('');
      setError(null);
      setPendingConflictPayload(null);
      setConflictingEvents([]);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleTypeSelect = (key) => {
    setFormData(prev => ({
      ...prev,
      event_type: key,
      // Auto-fill title suggestion
      title: prev.title || (key === 'other' ? '' : EVENT_TYPES[key].name),
      selected_client_id: key !== 'client_meeting' ? '' : prev.selected_client_id,
    }));
  };

  const buildNormalizedPayload = () => {
    const payload = { ...formData };
    if (formData.event_type === 'other') {
      const normalizedType = customTaskType.trim();
      const baseTitle = formData.title.trim();
      const startsWithType = baseTitle.toLowerCase().startsWith(normalizedType.toLowerCase());
      payload.title = startsWithType ? baseTitle : `${normalizedType}: ${baseTitle}`;
    }
    return payload;
  };

  const validateCurrentInput = () => {
    if (!formData.title.trim()) return 'Title is required';
    if (!formData.start_date) return 'Date is required';
    if (!formData.start_time) return 'Start time is required';
    if (formData.event_type === 'other' && !customTaskType.trim()) return 'Please add a custom task type';
    if (formData.event_type === 'client_meeting' && userRole === 'lawyer' && clients.length > 0 && !formData.selected_client_id) {
      return 'Please select a client for this meeting';
    }

    const now = new Date();
    const selectedStart = parseLocalDateTime(formData.start_date, formData.start_time);
    if (selectedStart && selectedStart < now) {
      return 'Start time cannot be in the past.';
    }

    if (formData.end_time) {
      const startMinutes = Number(formData.start_time.split(':')[0]) * 60 + Number(formData.start_time.split(':')[1]);
      const endMinutes = Number(formData.end_time.split(':')[0]) * 60 + Number(formData.end_time.split(':')[1]);
      if (endMinutes <= startMinutes) {
        return 'End time must be after start time.';
      }
    }

    return null;
  };

  const getConflictsForPayload = (payload) => {
    const currentRange = getRangeForDate(payload.start_date, payload.start_time, payload.end_time);
    if (!currentRange) return [];

    return (events || []).filter((ev) => {
      if (!ev?.start_date) return false;
      const status = String(ev.status || '').toLowerCase();
      if (CLOSED_EVENT_STATUSES.has(status)) return false;

      const evRange = getRangeForDate(ev.start_date, ev.start_time || '00:00', ev.end_time);
      if (!evRange) return false;

      return hasTimeOverlap(currentRange, evRange);
    });
  };

  const submitPayload = async (payload) => {
    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmAddAnyway = async () => {
    if (!pendingConflictPayload) return;
    setConflictingEvents([]);
    const payload = { ...pendingConflictPayload, __confirmedConflict: true };
    setPendingConflictPayload(null);
    await submitPayload(payload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateCurrentInput();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildNormalizedPayload();
    const conflicts = getConflictsForPayload(payload);

    if (conflicts.length > 0) {
      setPendingConflictPayload(payload);
      setConflictingEvents(conflicts);
      return;
    }

    await submitPayload(payload);
  };

  if (!isOpen) return null;

  const isClientMeeting = formData.event_type === 'client_meeting';
  const showClientPicker = isClientMeeting && userRole === 'lawyer' && clients.length > 0;
  const selectedClient = clients.find(c => c.id === formData.selected_client_id || c.client_id === formData.selected_client_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-clean" onClick={e => e.stopPropagation()}>
        <div className="modal-close-btn" onClick={onClose}><X size={22} /></div>

        <form onSubmit={handleSubmit} className="event-form-clean">

          {/* Event Type */}
          <div className="form-section">
            <label className="form-label">Task Type</label>
            <div className="event-type-grid">
              {Object.entries(EVENT_TYPES).map(([key, val]) => (
                <button
                  key={key}
                  type="button"
                  className={`event-type-btn ${formData.event_type === key ? 'active' : ''}`}
                  style={{
                    backgroundColor: formData.event_type === key ? val.color : 'var(--color-surface-secondary)',
                    borderColor: val.color,
                  }}
                  onClick={() => handleTypeSelect(key)}
                >
                  <span className="event-type-name">{val.name}</span>
                </button>
              ))}
            </div>
          </div>

          {formData.event_type === 'other' && (
            <div className="form-section">
              <label className="form-label">Custom Task Type *</label>
              <input
                type="text"
                value={customTaskType}
                onChange={(e) => setCustomTaskType(e.target.value)}
                placeholder="e.g. Research, Drafting, Follow-up"
                className="form-input"
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Client Picker — only for lawyer + client_meeting */}
          {showClientPicker && (
            <div className="form-section">
              <label className="form-label">
                <Users size={13} style={{ display: 'inline', marginRight: 5, verticalAlign: 'middle' }} />
                Select Client *
              </label>
              <div className="client-picker-grid">
                {clients.map(c => {
                  const cid = c.client_id || c.id;
                  const isSelected = formData.selected_client_id === cid;
                  return (
                    <button
                      key={cid}
                      type="button"
                      className={`client-picker-btn ${isSelected ? 'active' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, selected_client_id: cid }))}
                    >
                      <div className="client-picker-avatar">
                        {c.photo_url
                          ? <img src={c.photo_url} alt={c.name} onError={e => e.target.style.display = 'none'} />
                          : (c.name || c.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="client-picker-info">
                        <div className="client-picker-name">{c.name || c.email}</div>
                        {c.caseType && <div className="client-picker-case">{c.caseType}</div>}
                      </div>
                      {isSelected && <span className="client-picker-check">✓</span>}
                    </button>
                  );
                })}
              </div>
              {formData.selected_client_id && (
                <div className="client-selected-note">
                      📅 This task will also appear on the client's calendar
                </div>
              )}
            </div>
          )}

          {/* Date */}
          <div className="form-section">
            <label className="form-label">Date *</label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              className="form-input"
              disabled={isSubmitting}
            />
          </div>

          {/* Time */}
          <div className="form-section form-row">
            <div className="form-col">
              <label className="form-label">Start Time</label>
              <input type="time" name="start_time" value={formData.start_time} onChange={handleChange} className="form-input" disabled={isSubmitting} />
            </div>
            <div className="form-col">
              <label className="form-label">End Time</label>
              <input type="time" name="end_time" value={formData.end_time} onChange={handleChange} className="form-input" disabled={isSubmitting} />
            </div>
            <div className="form-col">
              <label className="form-label">Reminder</label>
              <select name="reminder_minutes" value={formData.reminder_minutes} onChange={handleChange} className="form-input" disabled={isSubmitting}>
                <option value={0}>None</option>
                <option value={5}>5 mins before</option>
                <option value={15}>15 mins before</option>
                <option value={30}>30 mins before</option>
                <option value={60}>1 hour before</option>
                <option value={1440}>1 day before</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div className="form-section">
            <label className="form-label">Task Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Task title"
              className="form-input"
              disabled={isSubmitting}
            />
          </div>

          {/* Location */}
          <div className="form-section">
            <label className="form-label">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g. Office, Court Room 3, Zoom..."
              className="form-input"
              disabled={isSubmitting}
            />
          </div>

          {/* Description */}
          <div className="form-section">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add details about this task..."
              rows={3}
              className="form-input form-textarea"
              disabled={isSubmitting}
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions-clean">
            <button type="button" className="btn-secondary-clean" onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="btn-primary-clean" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>

      {conflictingEvents.length > 0 && (
        <div className="conflict-modal-overlay" onClick={() => { setConflictingEvents([]); setPendingConflictPayload(null); }}>
          <div className="conflict-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Time Conflict Detected</h3>
            <p>You already have task(s) in this time range. Do you want to add this event anyway?</p>

            <div className="conflict-list">
              {conflictingEvents.slice(0, 4).map((ev) => (
                <div key={ev.id} className="conflict-item">
                  <div className="conflict-item-title">{ev.title || 'Untitled Task'}</div>
                  <div className="conflict-item-meta">
                    {ev.start_date} · {formatClock(ev.start_time)}{ev.end_time ? ` - ${formatClock(ev.end_time)}` : ''}
                  </div>
                </div>
              ))}
              {conflictingEvents.length > 4 && (
                <div className="conflict-more">+{conflictingEvents.length - 4} more conflicting event(s)</div>
              )}
            </div>

            <div className="conflict-actions">
              <button
                type="button"
                className="btn-secondary-clean"
                onClick={() => { setConflictingEvents([]); setPendingConflictPayload(null); }}
                disabled={isSubmitting}
              >
                Edit Time
              </button>
              <button
                type="button"
                className="btn-primary-clean"
                onClick={handleConfirmAddAnyway}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Add Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}